import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './useGuidedTour.css';
import { useGuidedTourContext } from '../context/GuidedTourContext';
import { updateAuthUser } from '../store/actions/Auth/AuthActions';
import { clearTourCompletion, markTourCompleted } from '../services/Auth/AuthService';

const DEFAULT_TOUR_START_DELAY_MS = 250;
const DEFAULT_STEP_HIGHLIGHT_PADDING = 6;
const TARGET_POLL_INTERVAL_MS = 200;
const TARGET_POLL_MAX_ATTEMPTS = 40;
// Guarantees a bit of breathing room around the target and the popover after
// driver.js positions them, so neither one sits flush against a fixed header
// or the bottom of the viewport.
const SCROLL_SAFETY_MARGIN_PX = 24;

/**
 * Walk up from `element` and return the nearest ancestor that is actually
 * scrollable on the Y axis. Bootstrap modals typically own their own scroll
 * on `.modal-dialog`, so scrolling the window does nothing when the target
 * lives inside a modal. Returning the ancestor lets nudgeScrollToFitStep
 * scroll the right container.
 */
const findScrollableAncestor = (element) => {
    let node = element?.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const isScrollable =
            (overflowY === 'auto' || overflowY === 'scroll') &&
            node.scrollHeight > node.clientHeight + 1;
        if (isScrollable) return node;
        node = node.parentElement;
    }
    return null;
};

/**
 * After driver.js has painted the target highlight and the popover, scroll the
 * target into the free band that the popover leaves visible. driver.js scrolls
 * the target to the middle of the viewport but does not reserve space for the
 * popover or the app's fixed header, so on short/narrow viewports the target
 * either sits under the header or the popover overlaps meaningful UI. We
 * measure both rects and center the target inside the region that is neither
 * covered by the header nor by the popover.
 *
 * When the target lives inside a scrollable ancestor (a modal, for example)
 * we scroll that ancestor rather than the window, since scrolling the window
 * would move nothing.
 */
const nudgeScrollToFitStep = (element) => {
    // Start each step from the popover's default (bottom-pinned on mobile)
    // position; the reflow check below will re-add the top-pinned class only
    // if this step's target ends up under the popover.
    document.querySelector('.driver-popover')?.classList.remove('milipot-popover-pinned-top');

    window.requestAnimationFrame(() => {
        if (!element || typeof element.getBoundingClientRect !== 'function') return;

        const viewportHeight = window.innerHeight;
        const stickyHeader = document.querySelector('.header');
        const headerBottom = stickyHeader?.getBoundingClientRect().bottom ?? 0;
        const topBound = Math.max(0, headerBottom) + SCROLL_SAFETY_MARGIN_PX;
        const bottomBound = viewportHeight - SCROLL_SAFETY_MARGIN_PX;

        const targetRect = element.getBoundingClientRect();
        const popover = document.querySelector('.driver-popover');
        const popoverRect = popover?.getBoundingClientRect() ?? null;

        // Pick the vertical band the target should land in. When the popover
        // sits below the target it eats the lower part of the viewport, so
        // the target belongs in the space above it. When the popover sits
        // above the target, the target belongs below it. When neither holds
        // (no popover yet, or popover overlaps the target), fall back to the
        // whole viewport minus the header.
        let bandTop = topBound;
        let bandBottom = bottomBound;
        if (popoverRect) {
            if (popoverRect.top >= targetRect.bottom) {
                bandBottom = Math.max(topBound + 1, popoverRect.top - SCROLL_SAFETY_MARGIN_PX);
            } else if (popoverRect.bottom <= targetRect.top) {
                bandTop = Math.min(bottomBound - 1, popoverRect.bottom + SCROLL_SAFETY_MARGIN_PX);
            }
        }

        const targetCenter = (targetRect.top + targetRect.bottom) / 2;
        const bandCenter = (bandTop + bandBottom) / 2;
        let delta = targetCenter - bandCenter;

        // If the target is taller than the band, we can't center it; align
        // its top just under the band's top edge instead so the head of the
        // target is visible.
        const targetHeight = targetRect.bottom - targetRect.top;
        if (targetHeight > bandBottom - bandTop) {
            delta = targetRect.top - bandTop;
        } else {
            if (targetRect.top - delta < bandTop) delta = targetRect.top - bandTop;
            if (targetRect.bottom - delta > bandBottom) delta = targetRect.bottom - bandBottom;
        }

        if (Math.abs(delta) >= 1) {
            const scrollableAncestor = findScrollableAncestor(element);
            if (scrollableAncestor) {
                scrollableAncestor.scrollBy({ top: delta, behavior: 'smooth' });
            } else {
                window.scrollBy({ top: delta, behavior: 'smooth' });
            }
        }

        // After the scroll (or the no-op when there is nothing to scroll)
        // settles, check whether the target is still covered by the popover.
        // If it is, toggle a class that flips the mobile bottom-pinned
        // popover to a top-pinned position, so the target stays visible
        // below it instead of trapped underneath.
        window.setTimeout(() => reflowPopoverIfOverlapping(element), 350);
    });
};

/**
 * If the target's top edge lands under the popover after the scroll settles
 * (typical on a mobile viewport where the popover is CSS-pinned to the
 * bottom and the container has no scroll to give), swap to a top-pinned
 * popover so the head of the target becomes visible. A target whose top
 * edge is already above the popover is left alone: for a tall element like
 * a full-height sidebar, flipping would hide the top part without gaining
 * anything, since the popover overlaps the sidebar in either position.
 */
const reflowPopoverIfOverlapping = (element) => {
    const popover = document.querySelector('.driver-popover');
    if (!element || !popover || typeof element.getBoundingClientRect !== 'function') return;

    const targetRect = element.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();

    const horizontallyOverlaps = !(popoverRect.right <= targetRect.left || popoverRect.left >= targetRect.right);
    const targetTopHiddenByPopover =
        horizontallyOverlaps &&
        targetRect.top >= popoverRect.top &&
        targetRect.top < popoverRect.bottom;

    if (targetTopHiddenByPopover) {
        popover.classList.add('milipot-popover-pinned-top');
    } else {
        popover.classList.remove('milipot-popover-pinned-top');
    }
};

// Advance the tour to the neighbour step only after that neighbour's
// onBeforeShow primer has resolved, so driver.js measures its target at
// its final size instead of a mid-animation snapshot. Used by the wrapped
// onNextClick and onPrevClick that buildDriverSteps generates when a
// neighbour step declares a primer.
const advanceAfterPrimer = (neighbourStep, moveFn) => {
    Promise.resolve(neighbourStep.onBeforeShow?.()).then(() => {
        if (typeof moveFn === 'function') moveFn();
    });
};

const buildDriverSteps = (steps) =>
    steps.map((step, index) => {
        const popover = {
            title: step.title,
            description: step.description,
            side: step.side ?? 'bottom',
            align: step.align ?? 'start',
        };
        // A step that supplied its own onNextClick is left alone. That handler
        // may destroy the driver or navigate away (the PIN handoff on the
        // security narrative step does exactly that), so awaiting a primer
        // afterwards would race with teardown. Otherwise, if the next step
        // needs the DOM primed before it shows, generate a wrapper that
        // awaits the primer and then advances driver.js manually. When
        // neither applies, driver.js keeps its default auto-advance.
        const nextStep = steps[index + 1];
        if (step.onNextClick) {
            popover.onNextClick = step.onNextClick;
        } else if (nextStep?.onBeforeShow) {
            popover.onNextClick = (element, driverStep, opts) => {
                advanceAfterPrimer(nextStep, opts?.driver?.moveNext?.bind(opts.driver));
            };
        }
        const prevStep = steps[index - 1];
        if (step.onPrevClick) {
            popover.onPrevClick = step.onPrevClick;
        } else if (prevStep?.onBeforeShow) {
            popover.onPrevClick = (element, driverStep, opts) => {
                advanceAfterPrimer(prevStep, opts?.driver?.movePrevious?.bind(opts.driver));
            };
        }
        if (step.nextBtnText) popover.nextBtnText = step.nextBtnText;
        if (step.prevBtnText) popover.prevBtnText = step.prevBtnText;
        const driverStep = { popover };
        if (step.element) driverStep.element = step.element;
        if (step.onHighlightStarted) driverStep.onHighlightStarted = step.onHighlightStarted;
        if (step.skipMissingElement) driverStep.skipMissingElement = step.skipMissingElement;
        return driverStep;
    });

const allStepTargetsExist = (steps) =>
    steps.every((step) => {
        if (!step.element) return true;
        // Steps that opt into skipMissingElement are allowed to have their
        // target missing at tour-start time; driver.js will silently skip
        // them at runtime. Without this branch the pre-start poll would
        // block the whole tour from firing whenever an optional target is
        // absent, for example a row action button on an empty list.
        if (step.skipMissingElement) return true;
        if (typeof step.element === 'function') return step.element() !== null;
        if (typeof step.element !== 'string') return true;
        return document.querySelector(step.element) !== null;
    });

/**
 * Runs a Driver.js guided tour the first time a user reaches a screen, and exposes a
 * restart function so a "Show me around again" affordance can replay it on demand.
 *
 * The "seen" flag lives on the user record on the API (completed_tour_ids), so a
 * user only sees each tour once across every device and browser.
 *
 * @param {string} tourId          Stable id used to record the "seen" flag on the user.
 * @param {Array}  steps           Ordered tour steps: { element, title, description, side?, align? }.
 * @param {object} [options]
 * @param {boolean}[options.enabled]           When false, auto-start is skipped (restart still works).
 * @param {number} [options.startDelayMs]      Delay before auto-starting, giving the DOM time to mount.
 * @param {string[]}[options.alsoMarkCompleted] Extra tour ids to mark completed alongside tourId
 *                                              when this tour finishes. Use it for a shared tail
 *                                              (the security outro) that several chains render, so
 *                                              a viewer belonging to two chains only sees the tail
 *                                              once. Ids already in completed_tour_ids are skipped.
 */
const useGuidedTour = (tourId, steps, options = {}) => {
    const { enabled = true, startDelayMs = DEFAULT_TOUR_START_DELAY_MS, alsoMarkCompleted = [] } = options;
    const dispatch = useDispatch();
    const authUser = useSelector((state) => state.auth.authUser);
    const completedTourIds = authUser?.completed_tour_ids ?? [];
    const hasCompletedThisTour = completedTourIds.includes(tourId);
    const driverInstanceRef = useRef(null);
    const pendingRetryTimeoutRef = useRef(null);
    const stepsRef = useRef(steps);
    stepsRef.current = steps;
    const completedTourIdsRef = useRef(completedTourIds);
    completedTourIdsRef.current = completedTourIds;
    // Kept in a ref so a caller passing a fresh array each render does not
    // reshape the recordTourCompletedOnServer callback identity, which would
    // in turn recreate runTour and cancel the auto start timer mid flight.
    const alsoMarkCompletedRef = useRef(alsoMarkCompleted);
    alsoMarkCompletedRef.current = alsoMarkCompleted;
    const userReachedEndRef = useRef(false);

    const clearPendingRetry = () => {
        if (pendingRetryTimeoutRef.current) {
            window.clearTimeout(pendingRetryTimeoutRef.current);
            pendingRetryTimeoutRef.current = null;
        }
    };

    const { registerTour, setTourActive } = useGuidedTourContext();

    const recordTourCompletedOnServer = useCallback(() => {
        const currentIds = completedTourIdsRef.current ?? [];
        const alsoMark = alsoMarkCompletedRef.current ?? [];
        const idsToAdd = [tourId, ...alsoMark].filter((id) => !currentIds.includes(id));
        if (idsToAdd.length === 0) return;

        dispatch(updateAuthUser({ completed_tour_ids: [...currentIds, ...idsToAdd] }));

        idsToAdd.forEach((idToPersist) => {
            markTourCompleted(idToPersist).catch((persistError) => {
                // Fire and forget: a failed write means the tour re-runs on next visit
                // until a subsequent completion sticks, which matches the old localStorage behaviour.
                // eslint-disable-next-line no-console
                console.warn('Could not record guided tour completion on the server.', persistError);
            });
        });
    }, [dispatch, tourId]);

    const clearTourCompletionOnServer = useCallback(() => {
        const currentIds = completedTourIdsRef.current ?? [];
        if (!currentIds.includes(tourId)) return;

        dispatch(
            updateAuthUser({
                completed_tour_ids: currentIds.filter((completedId) => completedId !== tourId),
            })
        );

        clearTourCompletion(tourId).catch((persistError) => {
            // eslint-disable-next-line no-console
            console.warn('Could not clear guided tour completion on the server.', persistError);
        });
    }, [dispatch, tourId]);

    const runTour = useCallback(() => {
        const currentSteps = stepsRef.current;
        if (!currentSteps || currentSteps.length === 0) return;
        clearPendingRetry();

        let attemptsRemaining = TARGET_POLL_MAX_ATTEMPTS;
        const attemptStart = () => {
            if (!allStepTargetsExist(currentSteps)) {
                attemptsRemaining -= 1;
                if (attemptsRemaining <= 0) return;
                pendingRetryTimeoutRef.current = window.setTimeout(attemptStart, TARGET_POLL_INTERVAL_MS);
                return;
            }

            const firstStepPrep = Promise.resolve(currentSteps[0]?.onBeforeShow?.());
            firstStepPrep.then(() => driveTour(currentSteps));
        };

        const driveTour = (stepsToDrive) => {
            userReachedEndRef.current = false;
            const driverInstance = driver({
                showProgress: true,
                allowClose: false,
                smoothScroll: true,
                stagePadding: DEFAULT_STEP_HIGHLIGHT_PADDING,
                popoverClass: 'milipot-driver-popover',
                nextBtnText: 'Next',
                prevBtnText: 'Back',
                doneBtnText: 'Got it',
                steps: buildDriverSteps(stepsToDrive),
                // The tour is "seen" as soon as the last step is highlighted.
                // We can't rely on onDestroyStarted here: driver.js's public
                // destroy() bypasses that hook and goes straight to teardown,
                // and our outro steps call destroy() themselves before
                // navigating. Marking on highlight covers every path that
                // reaches the final step, including the outro's Next click.
                //
                // The check lives on the config-level onHighlighted rather
                // than onHighlightStarted because driver.js's rule is
                // step-defined-hook-wins: if a step carries its own
                // onHighlightStarted (as the outro's sidebar-open primer
                // steps do), the config-level onHighlightStarted is
                // skipped for that step. No step in this app carries its
                // own onHighlighted, so putting the tracking here fires
                // reliably on every last step regardless of what the step
                // did on start.
                onHighlighted: (element, step, opts) => {
                    if (opts?.driver?.isLastStep?.()) {
                        userReachedEndRef.current = true;
                    }
                    nudgeScrollToFitStep(element);
                },
                onDestroyed: () => {
                    if (userReachedEndRef.current) {
                        recordTourCompletedOnServer();
                    }
                    userReachedEndRef.current = false;
                    setTourActive(false);
                },
            });
            driverInstanceRef.current = driverInstance;
            setTourActive(true);
            driverInstance.drive();
        };
        attemptStart();
    }, [recordTourCompletedOnServer, setTourActive]);

    const restart = useCallback(() => {
        clearTourCompletionOnServer();
        runTour();
    }, [clearTourCompletionOnServer, runTour]);

    useEffect(() => {
        const unregister = registerTour({ tourId, restart });
        return unregister;
    }, [registerTour, tourId, restart]);

    useEffect(() => {
        if (!enabled) return undefined;
        if (hasCompletedThisTour) return undefined;

        const timeoutId = window.setTimeout(runTour, startDelayMs);
        return () => {
            window.clearTimeout(timeoutId);
            clearPendingRetry();
            if (driverInstanceRef.current) {
                driverInstanceRef.current.destroy();
                driverInstanceRef.current = null;
            }
        };
    }, [enabled, hasCompletedThisTour, startDelayMs, runTour]);

    return { restart };
};

export default useGuidedTour;
